import { cva, type VariantProps } from 'class-variance-authority'
import type { ComponentPropsWithRef, ReactNode } from 'react'

import { cn } from '../../lib/utils'
import { Icon, type IconName } from '../icon'

/**
 * Button.
 *
 * Sizes and colours are the design's, exactly: 7px radius, 13px label, `accent` filled for the
 * one primary action a screen is allowed, `panel` over a `line` border for everything else.
 *
 * `danger` is its own variant rather than a colour prop, because the actions that need it —
 * removing a suppression, stopping sending, deleting a member — are ones §57 treats as auditable,
 * and they should not be one prop away from looking ordinary.
 *
 * `quiet` has no border at all. It is for the third and fourth action in a row, where a border on
 * each would turn a toolbar into a grid.
 */
const button = cva(
  'inline-flex items-center justify-center gap-2 rounded-[7px] font-sans whitespace-nowrap ' +
    'transition-colors disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary:
          'bg-accent border border-accent text-accent-on font-medium hover:bg-accent-hi hover:border-accent-hi',
        secondary: 'bg-panel border border-line text-text hover:bg-hover',
        quiet: 'bg-transparent border border-transparent text-muted hover:bg-hover hover:text-text',
        danger: 'bg-bad border border-bad text-bad-on font-medium hover:opacity-90',
        // A destructive action that is not the point of the screen: it reads as an outline until
        // you are on it.
        dangerQuiet: 'bg-transparent border border-line text-bad hover:bg-badbg hover:border-bad',
      },
      size: {
        sm: 'h-[26px] px-[9px] text-[12px]',
        md: 'h-[30px] px-3 text-[13px]',
        lg: 'h-[34px] px-[14px] text-[13px]',
        /** Square, for a lone glyph. The design uses 30×30 throughout the top bar. */
        icon: 'h-[30px] w-[30px] px-0',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
)

export interface ButtonProps
  extends ComponentPropsWithRef<'button'>,
    VariantProps<typeof button> {
  icon?: IconName
  /** Trailing glyph — a chevron on a menu trigger, never a second meaning. */
  trailingIcon?: IconName
  children?: ReactNode
}

export function Button({
  className,
  variant,
  size,
  icon,
  trailingIcon,
  children,
  ...props
}: ButtonProps) {
  return (
    <button className={cn(button({ variant, size }), className)} {...props}>
      {icon && <Icon name={icon} size={14} />}
      {children}
      {trailingIcon && <Icon name={trailingIcon} size={12} className="opacity-60" />}
    </button>
  )
}

/**
 * An icon-only button.
 *
 * Separate because it must never be reachable without a label — the whole content is a glyph, so
 * `label` is required and becomes both the accessible name and the tooltip.
 */
export function IconButton({
  icon,
  label,
  className,
  variant = 'secondary',
  size = 'icon',
  ...props
}: Omit<ButtonProps, 'children' | 'icon' | 'aria-label'> & { icon: IconName; label: string }) {
  return (
    <button
      aria-label={label}
      title={label}
      className={cn(button({ variant, size }), 'text-muted hover:text-text', className)}
      {...props}
    >
      <Icon name={icon} size={15} />
    </button>
  )
}
